// Aggregation for the Azure usage charts.
//
// Kept out of the component and free of state so the numbers can be computed
// straight from a fetch response: reading this.state right after setState
// gives the previous render's data, which is how the category chart ended up
// permanently one month behind.

const MISSING = "–"

// Sum a cost field over rows, grouped by another field. Rows whose key is
// missing are collected under one label rather than silently becoming
// "undefined" in the legend.
export const sumBy = (items, keyField, valueField = "totalCost") => {
  const totals = {}
  for (const item of items || []) {
    const value = Number(item?.[valueField])
    if (!Number.isFinite(value)) continue
    const rawKey = item?.[keyField]
    const key =
      rawKey === undefined || rawKey === null || rawKey === ""
        ? MISSING
        : String(rawKey)
    totals[key] = (totals[key] || 0) + value
  }
  // round once at the end: summing pre-rounded values drifts
  for (const key of Object.keys(totals)) {
    totals[key] = Math.round(totals[key] * 100) / 100
  }
  return totals
}

// Collapse a long tail into a single slice. A customer's usage can span
// nearly two hundred resource groups, which renders as an unreadable pie
// with more slices than the palette has colours.
export const topSlices = (data, limit = 10, otherLabel = "Other") => {
  const entries = Object.entries(data || {})
  if (entries.length <= limit) return { ...(data || {}) }

  entries.sort((a, b) => b[1] - a[1])
  const kept = entries.slice(0, limit)
  const rest = entries.slice(limit).reduce((sum, [, value]) => sum + value, 0)

  const out = {}
  for (const [key, value] of kept) out[key] = value
  const rounded = Math.round(rest * 100) / 100
  if (rounded > 0) out[otherLabel] = rounded
  return out
}

// Same aggregation as sumBy, shaped for a table: one row per group with the
// number of underlying usage records, largest spend first.
export const groupRows = (items, keyField, valueField = "totalCost") => {
  const totals = {}
  const counts = {}
  for (const item of items || []) {
    const value = Number(item?.[valueField])
    if (!Number.isFinite(value)) continue
    const rawKey = item?.[keyField]
    const key =
      rawKey === undefined || rawKey === null || rawKey === ""
        ? MISSING
        : String(rawKey)
    totals[key] = (totals[key] || 0) + value
    counts[key] = (counts[key] || 0) + 1
  }
  return Object.keys(totals)
    .map((key) => ({
      key,
      totalCost: Math.round(totals[key] * 100) / 100,
      count: counts[key],
    }))
    .sort((a, b) => b.totalCost - a.totalCost)
}

export default { sumBy, topSlices, groupRows }
